// Tek seçimli müşteri combobox'ı (2026-10-07) — ölçeklenen tek kalıp: yazdıkça 250 ms
// debounce ile sunucu araması (`GET /clients?search=&limit=20`), boşken "Son kullanılanlar"
// (localStorage, kullanıcı bazında, ≤5) + ilk sonuçlar + "Tüm müşteriler →". Seçili müşteri
// alan içinde çip (ad + ✕). ARIA combobox (listbox + aria-activedescendant), ok tuşları /
// Enter / Esc / Home / End; mobilde 44 px dokunma hedefi. İç içe etkileşimli öğe YOK
// (çipin ✕'i alanın değil, kardeş düğme — axe `nested-interactive`).
//
// `extraOptions` + `onExtra`: listenin sonuna sabit satırlar ("+ Yeni müşteri: Ad") —
// seçilince `onChange` DEĞİL `onExtra(key)` çağrılır. `suggestions`: boş sorguda "Öneriler"
// bölümü (rapor aktarımında kapak adına benzeyen kayıtlar). `renderOption`: ikincil satır.

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import {
  CLIENT_PICKER_COPY,
  buildPickerRows,
  isPickerNavKey,
  moveActive,
  pushRecentClient,
  readRecentClients,
  selectableRowIndexes,
  fillCopy,
  type ClientPickerItem,
} from "../../lib/client-picker";
import { useMediaQuery } from "../../lib/use-media-query";
import { useAuthStore } from "../../stores/auth-store";
import {
  ChipRemoveButton,
  ClientPickerListbox,
  chipStyle,
  chipTextStyle,
  fieldStyle,
  inputStyle,
  isInsidePickerList,
  optionDomId,
  useClientPickerSearch,
  type ClientPickerSize,
  type ClientPickerSourceProps,
} from "./ClientPickerCore";

export type { ClientPickerItem } from "../../lib/client-picker";

export interface ClientPickerProps extends ClientPickerSourceProps {
  value: ClientPickerItem | null;
  onChange: (value: ClientPickerItem | null) => void;
  /** Seçim ✕ ile temizlenebilir (varsayılan true). */
  allowEmpty?: boolean | undefined;
  placeholder?: string | undefined;
  size?: ClientPickerSize | undefined;
  /** Facet barında dar/düşük alan. */
  inline?: boolean | undefined;
  /** Erişilebilir ad (`<label htmlFor>` yoksa zorunlu). */
  ariaLabel?: string | undefined;
  /** Dış `<label htmlFor>` için input id'si. */
  id?: string | undefined;
  disabled?: boolean | undefined;
  autoFocus?: boolean | undefined;
  extraOptions?: readonly { key: string; label: string }[] | undefined;
  onExtra?: ((key: string) => void) | undefined;
  suggestions?: readonly ClientPickerItem[] | undefined;
  renderOption?: ((item: ClientPickerItem, defaultNode: ReactNode) => ReactNode) | undefined;
  /** Son kullanılanlar bölümü (varsayılan açık). */
  showRecent?: boolean | undefined;
  /** Seçilen kaydın `title`'ı (tam ad) — çip kesilirse. */
  title?: string | undefined;
}

export function ClientPicker(props: ClientPickerProps): JSX.Element {
  const {
    value,
    onChange,
    allowEmpty = true,
    placeholder = CLIENT_PICKER_COPY.placeholder,
    size = "md",
    inline = false,
    ariaLabel,
    id,
    disabled = false,
    autoFocus = false,
    extraOptions,
    onExtra,
    suggestions,
    renderOption,
    showRecent = true,
    allLink = "/clients",
    ...source
  } = props;
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const touch = useMediaQuery("(max-width: 768px)");
  const genId = useId();
  const inputId = id ?? `client-picker-${genId}`;
  const listId = `${inputId}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeRow, setActiveRow] = useState(-1);
  const [recent, setRecent] = useState<ClientPickerItem[]>([]);

  useEffect(() => {
    if (open && showRecent) setRecent(readRecentClients(userId));
  }, [open, showRecent, userId]);

  const search = useClientPickerSearch(source, query, open);
  const rows = useMemo(
    () =>
      buildPickerRows({
        query,
        recent: showRecent ? recent : [],
        suggestions,
        results: search.items,
        total: search.total,
        extraOptions,
      }),
    [query, recent, showRecent, suggestions, search.items, search.total, extraOptions],
  );
  const selectable = useMemo(() => selectableRowIndexes(rows), [rows]);

  // Satır dizisi değişince etkin satır geçersizleşebilir.
  useEffect(() => {
    setActiveRow((a) => {
      const r = a >= 0 ? rows[a] : undefined;
      return r !== undefined && r.kind !== "header" ? a : -1;
    });
  }, [rows]);

  // Dışarı tıklama kapatır.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (isInsidePickerList(e.target, listId)) return;
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function close(): void {
    setOpen(false);
    setActiveRow(-1);
  }

  function pick(item: ClientPickerItem): void {
    onChange(item);
    if (showRecent) setRecent(pushRecentClient(userId, item));
    setQuery("");
    close();
    inputRef.current?.focus();
  }

  function activate(rowIndex: number): void {
    const row = rows[rowIndex];
    if (!row) return;
    if (row.kind === "option") pick(row.item);
    else if (row.kind === "extra") {
      setQuery("");
      close();
      onExtra?.(row.key);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (disabled) return;
    if (isPickerNavKey(e.key)) {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const pos = selectable.indexOf(activeRow);
      const next = moveActive(pos, e.key, selectable.length);
      setActiveRow(next < 0 ? -1 : selectable[next]!);
      return;
    }
    if (e.key === "Enter") {
      if (open && activeRow >= 0) {
        e.preventDefault();
        activate(activeRow);
      } else if (!open) {
        e.preventDefault();
        setOpen(true);
      } else if (selectable.length === 1) {
        // Tek aday varken Enter onu seçer (klavye kısayolu).
        e.preventDefault();
        activate(selectable[0]!);
      }
      return;
    }
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        close();
      } else if (query) {
        e.preventDefault();
        setQuery("");
      }
      return;
    }
    if (e.key === "Backspace" && query.length === 0 && value && allowEmpty) {
      e.preventDefault();
      onChange(null);
      return;
    }
    if (e.key === "Tab") close();
  }

  const name = ariaLabel ?? placeholder;
  const activeId = open && activeRow >= 0 ? optionDomId(listId, activeRow) : undefined;

  return (
    <div
      ref={rootRef}
      style={{ position: "relative", minWidth: 0, flex: inline ? "0 1 auto" : "1 1 auto" }}
    >
      <div
        style={fieldStyle({ size, inline, focused, touch, disabled })}
        onClick={() => {
          if (disabled) return;
          inputRef.current?.focus();
          setOpen(true);
        }}
      >
        {value ? (
          <span style={chipStyle(size)} title={props.title ?? value.name} data-client-chip>
            <span style={chipTextStyle}>{value.name}</span>
            {allowEmpty && !disabled ? (
              <ChipRemoveButton
                label={`${CLIENT_PICKER_COPY.clear}: ${value.name}`}
                onClick={() => {
                  onChange(null);
                  inputRef.current?.focus();
                }}
                touch={touch}
              />
            ) : null}
          </span>
        ) : null}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-label={name}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-haspopup="listbox"
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={value ? "" : placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveRow(-1);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
          style={{
            ...inputStyle(size),
            ...(value && !query && !focused ? { width: 24, flex: "0 0 24px" } : {}),
          }}
        />
        <span
          aria-hidden="true"
          style={{
            color: "var(--color-text-3)",
            fontSize: "0.7rem",
            padding: "0 2px",
            flexShrink: 0,
          }}
        >
          ▾
        </span>
      </div>
      {open && !disabled ? (
        <ClientPickerListbox
          id={listId}
          anchorRef={rootRef}
          label={name}
          rows={rows}
          activeRow={activeRow}
          selectedIds={new Set(value ? [value.id] : [])}
          query={query}
          loading={search.loading}
          error={search.error}
          onRetry={search.retry}
          onPick={pick}
          onExtra={(key) => activate(rows.findIndex((r) => r.kind === "extra" && r.key === key))}
          renderOption={renderOption}
          allLink={allLink}
          touch={touch}
          size={size}
        />
      ) : null}
      {/* Ekran okuyucu: seçim durumu (çip metni görsel; input değeri sorgudur). */}
      <span className="sr-only" aria-live="polite">
        {value ? fillCopy(CLIENT_PICKER_COPY.selectedCount, { n: 1 }) + `: ${value.name}` : ""}
      </span>
    </div>
  );
}
